import React, { useEffect, useState } from 'react'
import { Box, Skeleton } from '@mui/material'
import { getIconUrl } from '../../services/static_data'
import { CustomBreakpoints, useCustomBreakpoint } from './Charts'
import { formatDollarValue } from './chartUtils'
import { NUMBER_COLUMN_MIN_WIDTH, TableCell, TableHeaderRow, TableHeaderText, TableRow, TableText } from './commonComponents'
import { TableContainer } from './TableContainer'
import { HelptipIcon } from '../../svgIcons/HelptipIcon'
import { useStoreActions } from '../../model/hooks'
import { useNavigate } from 'react-router'
import { formatNumber } from '../../blockchainUtils/math'
import { PoolData, PoolOverview, loadPoolOverviewData, loadPoolsData } from './chartsApi'
import { SearchIcon } from '../../svgIcons/SearchIcon'
import { SpaceshipIcon } from '../../svgIcons/SpaceshipIcon'
import { PrecentChange } from './PrecentChange'
import BigNumber from 'bignumber.js'
import { RewardBadgeIcon } from '../../svgIcons/RewardBadgeIcon'
// import { tokenAddresses } from '../IncentivePage/IncentivePage'
import { AprStarIcon } from '../../svgIcons/AprStarIcon'
import { possiblePools } from '../PositionsV3'

type Column = '#' | 'POOL' | 'TVL' | 'VOLUME_24H' | 'VOLUME_7D' | 'APR' | 'CL_APR'

export const PoolsTable = ({ tokenAddress }: { tokenAddress?: string }) => {
    const size = useCustomBreakpoint()
    const navigate = useNavigate()
    const loadTokens = useStoreActions((actions) => actions.tokens.loadTokens)
    const [data, setData] = useState([] as (PoolOverview & PoolData)[])
    const [poolOverviews, setPoolsOverview] = useState(undefined as unknown as (PoolOverview & PoolData)[])
    const [searchTerm, setSearchTerm] = useState('')
    const [isInputFocused, setIsInputFocused] = useState(false)
    const [isNoResults, setIsNoResults] = useState(false)

    useEffect(() => {
        if (poolOverviews) updateData()
    }, [poolOverviews])

    useEffect(() => {
        updateData()
    }, [searchTerm])

    useEffect(() => {
        loadTokens({ network: 'mainnet-alpha' })
    }, [])

    useEffect(() => {
        loadAllPoolsData()
        // loadOverview()
    }, [tokenAddress])

    async function loadAllPoolsData() {
        const response = await loadPoolsData()

        const promises: any[] = []
        response.map((pool) => promises.push(loadPoolOverviewData(pool.poolkey)))

        const overviews: PoolOverview[] = await Promise.all(promises)
        const combinedData: any[] = []
        response.map((item, index) => {
            if ((tokenAddress && item.token0.address.toLowerCase() === tokenAddress?.toLowerCase()) || item.token1.address.toLowerCase() === tokenAddress?.toLowerCase())
                combinedData.push({ ...overviews[index], ...response[index] })
            else if (!tokenAddress) combinedData.push({ ...overviews[index], ...response[index] })
        })

        setPoolsOverview(combinedData)
    }

    function updatePoolsWithoutBannedTokens(data: (PoolOverview & PoolData)[]) {
        const newData = [] as (PoolOverview & PoolData)[]

        data.map((item) => {
            if (item.poolkey.toLowerCase() !== '0x452add90bc93d620f2ea539fa16276b437445314e76ed23b17ac2e67395c99'.toLowerCase()) newData.push(item)
        })
        setData(newData)
    }

    function updateData() {
        if (!poolOverviews) return

        let overviews = [...poolOverviews]
        if (searchTerm) {
            const searchTermLowerCased = searchTerm.toLowerCase()
            overviews = poolOverviews.filter(
                (option) =>
                    option.poolkey.toLowerCase().includes(searchTermLowerCased) ||
                    option.token0.symbol.toLowerCase().includes(searchTermLowerCased) ||
                    option.token0.address.toLowerCase().includes(searchTermLowerCased) ||
                    option.token1.symbol.toLowerCase().includes(searchTermLowerCased) ||
                    option.token1.address.toLowerCase().includes(searchTermLowerCased)
            )
        }
        const poolsData: (PoolOverview & PoolData)[] = [...overviews]
        poolsData.sort((a, b) => {
            if (a.tvl > b.tvl) return -1
            if (b.tvl > a.tvl) return 1
            return 0
        })

        if (poolsData.length === 0) setIsNoResults(true)
        else setIsNoResults(false)

        if (!searchTerm) return updatePoolsWithoutBannedTokens(poolsData)
        else setData(poolsData)
    }

    let columns: Array<Column>
    if (size === CustomBreakpoints.XSmall) columns = ['POOL', 'VOLUME_24H', 'APR']
    else if (size === CustomBreakpoints.Small) columns = ['POOL', 'VOLUME_24H', 'VOLUME_7D']
    else if (size === CustomBreakpoints.Medium) columns = ['POOL', 'VOLUME_24H', 'VOLUME_7D']
    else if (size === CustomBreakpoints.Large) columns = ['POOL', 'VOLUME_24H', 'VOLUME_7D', 'CL_APR']
    else columns = ['#', 'POOL', 'TVL', 'VOLUME_24H', 'VOLUME_7D', 'APR', 'CL_APR']

    function renderSearch() {
        return (
            <div className={`max-w-[400px] mb-4 flex justify-between mt-6 w-full relative ${isInputFocused ? '#0AE8F9' : 'text-[#8387A5]'} text-[16px]`}>
                <input
                    value={searchTerm}
                    onBlur={() => {
                        setIsInputFocused(false)
                    }}
                    onFocus={() => {
                        setIsInputFocused(true)
                    }}
                    onChange={(e) => {
                        setSearchTerm(e.target.value)
                    }}
                    className='focus:border-[#0AE8F9] border-[1px] rounded-[8px] px-4 py-2  border-[#8387A5]  w-full h-full mr-2 outline-none bg-transparent'
                    placeholder='Search Pool by token name or symbol'
                />
                <SearchIcon className='absolute right-[16px] top-[10px] w-[18px] h-[18px]' />
            </div>
        )
    }

    function getToken1Name(pool: PoolOverview & PoolData) {
        const symbol = pool.token0.symbol === 'WSTETH' ? 'wstETH' : pool.token0.symbol
        return symbol
    }

    function getToken2Name(pool: PoolOverview & PoolData) {
        const symbol = pool.token1.symbol === 'WSTETH' ? 'wstETH' : pool.token1.symbol
        return symbol
    }

    function renderIncentiveBadge(token1: string, token2: string) {
        // const possiblePools = [
        //     [tokenAddresses.ETH, tokenAddresses.USDC],
        //     [tokenAddresses.USDC, tokenAddresses.USDT],
        //     [tokenAddresses.STRK, tokenAddresses.ETH],
        //     [tokenAddresses.STRK, tokenAddresses.USDC]
        // ]

        const isMatch = possiblePools.some((pool) => [token1, token2].every((token) => pool.includes(token)))

        if (!isMatch) return null
        return (
            <div className='relative flex items-center ml-2'>
                <div className='group flex'>
                    <RewardBadgeIcon className='w-[24px] h-[24px]' />
                    <span
                        style={{ background: 'rgba(255, 255, 255, 0.85' }}
                        className='w-[180px] left-[17px] group-hover:visible invisible group-hover:opacity-100 transition-opacity text-sm text-black rounded-md absolute opacity-0 top-[-36px] p-3 z-[1]'>
                        Eligible for incentive
                    </span>
                </div>
            </div>
        )
    }

    function renderEmptyTableSearch() {
        return (
            <div className='flex flex-col min-h-[600px]  text-white items-center mt-[48px]'>
                <SpaceshipIcon className='w-[60px] h-[60px]' />
                <span className='mt-2 text-[18px] font-bold'>Pool not found</span>
            </div>
        )
    }

    function renderContent() {
        if (isNoResults) return renderEmptyTableSearch()

        return (
            <TableContainer
                className='min-h-[600px] '
                // title={'Pools'}
                headerRow={
                    <TableHeaderRow style={{ padding: '0 16px 0px 16px' }}>
                        {columns.map(
                            (col) =>
                                ({
                                    '#': (
                                        <TableCell key={col} sx={{ minWidth: NUMBER_COLUMN_MIN_WIDTH }}>
                                            <TableHeaderText>#</TableHeaderText>
                                        </TableCell>
                                    ),
                                    POOL: (
                                        <TableCell main key={col}>
                                            <TableHeaderText>Pool</TableHeaderText>
                                        </TableCell>
                                    ),
                                    TVL: (
                                        <TableCell floatRight key={col}>
                                            <TableHeaderText>TVL</TableHeaderText>
                                        </TableCell>
                                    ),
                                    // TVL_24H: (
                                    //     <TableCell floatRight key={col}>
                                    //         <TableHeaderText>TVL 24H</TableHeaderText>
                                    //     </TableCell>
                                    // ),
                                    VOLUME_24H: (
                                        <TableCell floatRight key={col}>
                                            <TableHeaderText>Volume 24H</TableHeaderText>
                                        </TableCell>
                                    ),
                                    VOLUME_7D: (
                                        <TableCell floatRight key={col}>
                                            <TableHeaderText>Volume 7d</TableHeaderText>
                                        </TableCell>
                                    ),
                                    APR: (
                                        <TableCell className='relative ' floatRight key={col}>
                                            <TableHeaderText>APR</TableHeaderText>
                                            <div className='ml-2 group inline-flex'>
                                                <HelptipIcon className='w-5 h-5' />
                                                {/* <span className="bg-red-400 text-white px-2 py-1">Button</span> */}
                                                <span className='right-0 top-[-45px] w-[200px] group-hover:visible invisible group-hover:opacity-100 transition-opacity bg-[#242C58] text-sm text-gray-100 rounded-md absolute opacity-0 top-[30px] p-3 z-[1]'>
                                                    {`Estimate the Annualized Percentage Revenue (APR) by annualizing daily fees and dividing by the pool's TVL.`}
                                                    {/* {'7-day moving average'} */}
                                                </span>
                                            </div>
                                        </TableCell>
                                    ),
                                    CL_APR: (
                                        <TableCell className='relative ' floatRight key={col}>
                                            <TableHeaderText>CL APR</TableHeaderText>
                                            <div className='ml-2 group inline-flex'>
                                                <HelptipIcon className='w-5 h-5' />
                                                <span className='right-0 top-[-45px] w-[200px] group-hover:visible invisible group-hover:opacity-100 transition-opacity bg-[#242C58] text-sm text-gray-100 rounded-md absolute opacity-0 top-[30px] p-3 z-[1]'>
                                                    {
                                                        'Estimate the Annualized Percentage Revenue (APR) by annualizing daily fees and dividing by the TVL in a selected range. Hover over a pool for specific range details. '
                                                        // 'Estimate the Annualized Percentage Revenue (APR) by annualizing daily fees and dividing by the TVL in a selected range. Hover over a pool for specific range details. '
                                                    }
                                                </span>
                                            </div>
                                        </TableCell>
                                    )
                                }[col])
                        )}
                    </TableHeaderRow>
                }
                items={(data ?? ([] as (PoolOverview & PoolData)[])).slice(0, 20)}
                skeleton={<RowSkeleton columns={columns} />}
                renderItem={(pool, idx) => (
                    <TableRow
                        className='cursor-pointer flex items-center lg:hover:rounded-[16px] lg:hover:bg-[#294C81]'
                        onClick={() => navigate(`/charts/${pool.poolkey}`)}
                        key={pool.token0.symbol + pool.token1.symbol + pool.fee}>
                        {columns.map((col) => {
                            switch (col) {
                                case '#':
                                    return (
                                        <TableCell key={col} sx={{ minWidth: NUMBER_COLUMN_MIN_WIDTH }}>
                                            <TableText>{idx + 1}</TableText>
                                        </TableCell>
                                    )
                                case 'POOL':
                                    return (
                                        <TableCell main key={col}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}>
                                                <img style={{}} src={getIconUrl('mainnet-alpha', pool.token0.address)} alt='' width={24} height={24} />
                                                <img
                                                    style={{
                                                        marginRight: 8,
                                                        marginLeft: -8
                                                    }}
                                                    src={getIconUrl('mainnet-alpha', pool.token1.address)}
                                                    alt=''
                                                    width={24}
                                                    height={24}
                                                />
                                                <TableText
                                                    sx={{
                                                        display: 'inline',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                    {getToken1Name(pool)}/{getToken2Name(pool)}
                                                </TableText>
                                                {renderIncentiveBadge(pool.token0.address, pool.token1.address)}
                                                <div className='ml-2 text-[#0AE8F9] bg-[#0C3F6D] p-1 rounded-xl text-[8px] lg:text-[16px]'>{pool.pool_fee + '%'}</div>
                                            </Box>
                                        </TableCell>
                                    )
                                case 'TVL':
                                    return (
                                        <TableCell floatRight key={col}>
                                            {pool.tvl_change_24h ? (
                                                <PrecentChange marginRight={2} className='mr-2' fontSize={12} lgFontSize={12} value={new BigNumber(pool.tvl_change_24h || 0)} />
                                            ) : null}
                                            <TableText>${formatDollarValue(pool.tvl ?? '0')}</TableText>
                                        </TableCell>
                                    )
                                // case 'TVL_24H':
                                //     return (
                                //         <TableCell floatRight key={col}>
                                //             <PrecentChange fontSize={16} lgFontSize={18} value={new BigNumber(pool.tvl_change_24h || 0)} />

                                //             {/* <TableText>{numFormat(pool.tvl_change_24h)}</TableText> */}
                                //         </TableCell>
                                //     )
                                case 'VOLUME_24H':
                                    return (
                                        <TableCell floatRight key={col}>
                                            <TableText>${formatDollarValue(pool.volume_24h ?? '0')}</TableText>
                                        </TableCell>
                                    )
                                case 'VOLUME_7D':
                                    return (
                                        <TableCell floatRight key={col}>
                                            <TableText>${formatDollarValue(pool.volume_7d ?? '0')}</TableText>
                                        </TableCell>
                                    )
                                case 'APR': {
                                    let apr = (pool && pool.apr && pool.apr.apr ? pool.apr.apr : 0) as number
                                    if (pool.apr.incentive_apr) apr = apr + pool.apr.incentive_apr
                                    return (
                                        <TableCell floatRight key={col}>
                                            <div className='flex relative items-center'>
                                                <TableText style={{ color: Number(apr) > 10 ? '#1BFF31' : '#68F2B0' }}>{formatNumber(apr)}%</TableText>
                                                {pool.apr.incentive_apr ? (
                                                    <div className='ml-2 group inline-flex'>
                                                        <AprStarIcon className='ml-1' />
                                                        <span
                                                            style={{ background: 'rgba(255, 255, 255, 0.85)' }}
                                                            className='flex flex-col right-0 top-[-45px] w-[240px] group-hover:visible invisible group-hover:opacity-100 transition-opacity text-[16px] text-black rounded-md absolute opacity-0 top-[30px] p-3 z-[1]'>
                                                            <span>{`Total Apr: ${apr.toFixed(2)}%`}</span>
                                                            <span className='font-bold'>{`DeFi Incentives: ${pool.apr.incentive_apr.toFixed(2)}%`}</span>
                                                        </span>
                                                    </div>
                                                ) : null}
                                                {/* {pool.apr.incentive_apr ? <span className='text-[#0AE8F9] text-[12px] font-bold mt-1'>{`(${pool.apr.incentive_apr.toFixed(2)}% DeFi Incent.)`}</span> : null} */}
                                            </div>
                                        </TableCell>
                                    )
                                }
                                case 'CL_APR': {
                                    // return <></>
                                    // const apr = Number(calculateAPR24(pool.volume_24h.toNumber(), pool.pool_fee, pool.tvl.toNumber()))
                                    let apr = (pool && pool.apr && pool.apr.apr ? pool.apr.apr_cl : 0) as number
                                    if (pool.apr.incentive_apr) apr = apr + pool.apr.incentive_apr

                                    // const apr = Number(numFormat(pool.apr.apr_cl.toString())).toFixed(2)
                                    return (
                                        <TableCell floatRight key={col}>
                                            <div className='ml-2 inline-flex'>
                                                <div className='relative group/help inline-flex text-[18px] font-[400]'>
                                                    <span className='inline-flex' style={{ color: Number(apr) > 10 ? '#1BFF31' : '#68F2B0' }}>
                                                        {formatNumber(apr)}%
                                                    </span>
                                                    <span className='right-0 top-[-45px] w-[200px] group-hover/help:visible invisible group-hover/help:opacity-100 transition-opacity bg-[#242C58] text-sm text-gray-100 rounded-md absolute opacity-0 top-[30px] p-3 z-[1]'>
                                                        {
                                                            `Range for CL APR calculation: +/- ${pool.apr && pool.apr.percentage ? pool.apr.percentage * 100 : 0}%`
                                                            // 'Estimate the Annualized Percentage Revenue (APR) by annualizing daily fees and dividing by the TVL in ap selected range. Hover over a pool for specific range details. '
                                                        }
                                                    </span>
                                                </div>
                                                <div className='flex items-center'>
                                                    {pool.apr.incentive_apr ? (
                                                        <div className='relative ml-2 group/item inline-flex'>
                                                            <AprStarIcon className='ml-1' />
                                                            <span
                                                                style={{ background: 'rgba(255, 255, 255, 0.85)' }}
                                                                className='flex flex-col right-0 top-[-45px] w-[240px] group-hover/item:visible invisible group-hover/item:opacity-100 transition-opacity text-[16px] text-black rounded-md absolute opacity-0 top-[30px] p-3 z-[1]'>
                                                                <span>{`Total Apr: ${apr.toFixed(2)}%`}</span>
                                                                <span className='font-bold'>{`DeFi Incentives: ${pool.apr.incentive_apr.toFixed(2)}%`}</span>
                                                            </span>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </TableCell>
                                    )
                                }
                                default:
                                    return null
                            }
                        })}
                    </TableRow>
                )}
            />
        )
    }

    return (
        <>
            <div className='flex flex-col'>
                <span className='pl-[16px] text-def font-bold text-white block'>{'Top Pools'}</span>
                {renderSearch()}
            </div>
            {renderContent()}
        </>
    )
}

// fee 0.003

const RowSkeleton = ({ columns }: { columns: Column[] }) => {
    return (
        <TableRow>
            {columns.map((col: Column) => {
                switch (col) {
                    case '#':
                        return (
                            <TableCell key={col}>
                                <TableText>
                                    <Skeleton width={10}></Skeleton>
                                </TableText>
                            </TableCell>
                        )
                    case 'POOL':
                        return (
                            <TableCell main key={col}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                    <Skeleton variant='circular' width={24} height={24} sx={{ mr: 2 }} />

                                    <TableText>
                                        <Skeleton width={60} sx={{ mr: 1 }}></Skeleton>
                                    </TableText>
                                    <TableText>
                                        <Skeleton width={60}></Skeleton>
                                    </TableText>
                                </Box>
                            </TableCell>
                        )
                    default:
                        return (
                            <TableCell floatRight key={col}>
                                <TableText>
                                    <Skeleton width={60}></Skeleton>
                                </TableText>
                            </TableCell>
                        )
                }
            })}
        </TableRow>
    )
}
